import React, { useEffect, useRef, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { ExtendedWindowType } from '../coveo/engine';
function Pagination({
  totalPages,
  pagesToShow,
  currentPage,
  onPageChange,
  nextPageLabel,
  previousPageLabel,
  pageLabel
}: InferProps<typeof Pagination.propTypes>) {
  const pages =
    pagesToShow || Object.keys([...Array(totalPages)]).map(i => Number(i) + 1);
  const paginationRef = useRef<HTMLUListElement>(null);
  const [parentSection, setParentSection] = useState<HTMLElement | null>();

  const scrollToTop = () => {
    if (parentSection) {
      setTimeout(() => {
        parentSection.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    }
  };

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    const page = Number(event.currentTarget.value);
    event.currentTarget.blur();
    onPageChange(page);
    scrollToTop();

    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event: 'pagination_click',
      event_category: 'pagination',
      event_action: 'click',
      event_label: page
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
      scrollToTop();

      const global = window as ExtendedWindowType;

      global.dataLayer?.push({
        event: 'pagination_click',
        event_category: 'pagination',
        event_action: 'click',
        event_label: 'previous'
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
      scrollToTop();

      const global = window as ExtendedWindowType;

      global.dataLayer?.push({
        event: 'pagination_click',
        event_category: 'pagination',
        event_action: 'click',
        event_label: 'next'
      });
    }
  };

  const renderPage = (pageNumber: number, active: boolean) => {
    return (
      <li>
        <button
          className={`pagination__page ${active ? 'active' : ''}`}
          value={pageNumber}
          onClick={handlePageChange}
          aria-label={pageNumber.toString()}
        >
          <span className="sr-only">{pageLabel} </span>
          {pageNumber}
        </button>
      </li>
    );
  };

  const renderEllipsis = () => (
    <li>
      <span role="presentation" className="pagination__ellipsis">
        {'...'}
      </span>
    </li>
  );

  useEffect(() => {
    if (paginationRef.current) {
      const parentSection = paginationRef.current.closest('section');
      parentSection?.style.setProperty(
        'scroll-margin-block-start',
        'calc(var(--header-block-size) + 100px)'
      );
      setParentSection(parentSection);
    }
  }, [paginationRef.current]);

  if (totalPages > 1) {
    return (
      <ul className="pagination" ref={paginationRef}>
        <li>
          <button
            className="pagination__prev"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            title={previousPageLabel}
            aria-label={previousPageLabel}
          >
            <svg>
              <use xlinkHref="#chevron-left" />
            </svg>
          </button>
        </li>

        {renderPage(1, currentPage === 1)}

        {currentPage > 4 && totalPages > 5 && <>{renderEllipsis()}</>}

        {totalPages <= 5 &&
          pages.map((page, index) => (
            <React.Fragment key={index}>
              {page > 1 && page < totalPages && (
                <>{renderPage(page, currentPage === page)}</>
              )}
            </React.Fragment>
          ))}

        {totalPages > 5 &&
          pages.map((page, index) => {
            if (currentPage <= 4 && page <= 4 && page > 1) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }

            if (
              currentPage > 4 &&
              page >= currentPage - 1 &&
              page <= currentPage + 1 &&
              page < totalPages
            ) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }

            if (
              currentPage >= totalPages - 1 &&
              page >= totalPages - 3 &&
              page < totalPages
            ) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }
            return null;
          })}

        {totalPages === 6 && currentPage + 2 <= totalPages && (
          <>{renderEllipsis()}</>
        )}

        {totalPages > 6 && currentPage + 3 <= totalPages && (
          <>{renderEllipsis()}</>
        )}

        {renderPage(totalPages, currentPage === totalPages)}
        <li>
          <button
            onClick={handleNextPage}
            className="pagination__next"
            disabled={currentPage === totalPages}
            title={nextPageLabel}
            aria-label={nextPageLabel}
          >
            <svg>
              <use xlinkHref="#chevron-right" />
            </svg>
          </button>
        </li>
      </ul>
    );
  } else {
    return null;
  }
}

Pagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  pagesToShow: PropTypes.array,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  nextPageLabel: PropTypes.string.isRequired,
  previousPageLabel: PropTypes.string.isRequired,
  pageLabel: PropTypes.string.isRequired
};

export default Pagination;
